import React, { useState, useEffect, useRef, useCallback } from "react";
import { CustomCard, CustomCardTitle } from "@repo/ui";
import LazyLoadImage from "../../../components/LazyLoadImage/LazyLoadImage";
import withAnimation from "../../../hoc/withCardAnimation/withCardAnimation";
import placeholderImage from "../../../assets/placeholders/picture.webp";
import { GiftTableProduct } from "../../../redux/slices/giftTableSlice";
import { getProduct, Product } from "../../../services/productApiService";
import { MdStars } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { withTranslations } from "../../../hoc/withTranslations/withTranslations";

const AnimatedCard = withAnimation(CustomCard);

interface LazyMyProductCardProps {
  localProduct: GiftTableProduct;
  onProductSelect: (product: { local: GiftTableProduct; api: Product }) => void;
}

const LOCALE = "modules_MyGiftTable_components_LazyMyProductCard";

const LazyMyProductCard: React.FC<LazyMyProductCardProps> = ({
  localProduct,
  onProductSelect,
}) => {
  const { t } = useTranslation(LOCALE);
  const [productData, setProductData] = useState<Product | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);

  const fetchProductData = useCallback(async () => {
    const data = await getProduct(localProduct.productID);
    setProductData(data);
  }, [localProduct.productID]);

  const onIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    if (entry.isIntersecting) setIsVisible(true);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(onIntersection, {
      rootMargin: "100px",
      threshold: 0.1,
    });
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [onIntersection]);

  useEffect(() => {
    if (isVisible && !productData) fetchProductData();
  }, [isVisible, productData, fetchProductData]);

  const handleClick = () => {
    if (productData) onProductSelect({ local: localProduct, api: productData });
  };

  const firstPicture =
    productData?.cover_picture?.trim() ||
    productData?.pictures[0]?.trim() ||
    placeholderImage;

  const renderAlreadyYours = () => {
    if (localProduct.taken > 1) {
      return t("alreadyYours_plural", { count: localProduct.taken });
    } else {
      return t("alreadyYours", { count: localProduct.taken });
    }
  };

  return (
    <div
      ref={cardRef}
      className="relative group cursor-pointer"
      onClick={handleClick}
    >
      <AnimatedCard className="relative rounded-s shadow-none border transition-transform transform group-hover:scale-102 group-hover:shadow-lg w-full">
        {localProduct.taken > 0 && (
          <div className="absolute top-2 left-2 bg-green-500 text-white text-xs font-semibold px-2 py-1 rounded">
            {renderAlreadyYours()}
          </div>
        )}
        {localProduct.priority && (
          <MdStars className="absolute top-2 right-2 h-6 w-6 text-secondary" />
        )}
        <LazyLoadImage
          src={firstPicture}
          alt={productData?.title || t("loadingAlt")}
          className="w-full h-64 md:h-96 object-contain"
        />
        <div className="p-4">
          {productData ? (
            <>
              <p className="text-lg font-bold text-primary">
                {productData.brand}
              </p>
              <CustomCardTitle>{productData.title}</CustomCardTitle>
              <p className="text-lg font-semibold text-primary mt-2">
                {productData.display_price?.toLocaleString("es-MX", {
                  style: "currency",
                  currency: productData.display_currency,
                })}
              </p>
              <p className="text-sm text-gray-600 mt-2">
                {t("tableQuantity")}:{" "}
                <span className="font-bold">{localProduct.quantity}</span>
              </p>
            </>
          ) : (
            <p className="text-gray-400 text-sm">{t("loadingAlt")}</p>
          )}
        </div>
      </AnimatedCard>
    </div>
  );
};

export default withTranslations(LOCALE)(LazyMyProductCard);
